import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import apiClient from "../lib/api";
import { Crown, X, Lock, Lightning, Star, ChartLineUp } from "@phosphor-icons/react";

const SESSION_DISMISS_KEY = "pickvip_modal_dismissed_v2";

function usePublicWinRate() {
  const [rate, setRate] = useState(null);
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    fetch(`${base}/api/public/stats`)
      .then(r => r.json())
      .then(d => setRate(d?.win_rate ?? null))
      .catch(() => {});
  }, []);
  return rate;
}

export default function PickVipFeaturedModal() {
  const [pick, setPick]       = useState(null);
  const [open, setOpen]       = useState(false);
  const [closing, setClosing] = useState(false);
  const [betAmount, setBetAmount] = useState(50);
  const winRate = usePublicWinRate();

  useEffect(() => {
    let cancelled = false;
    if (sessionStorage.getItem(SESSION_DISMISS_KEY) === "1") return;

    (async () => {
      try {
        const res = await apiClient.get("/picks?status=pending&limit=40");
        if (cancelled) return;
        const all = res.data?.picks || [];
        const now = Date.now();
        const candidate = all.find(p =>
          p.is_paid_pick &&
          p.pick_price &&
          p.status === "pending" &&
          p.locked === true &&
          (!p.match_date || new Date(p.match_date).getTime() > now)
        );
        if (candidate) {
          setPick(candidate);
          setTimeout(() => !cancelled && setOpen(true), 900);
        }
      } catch (_) {}
    })();

    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    setClosing(true);
    sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    setTimeout(() => setOpen(false), 280);
  };

  if (!open || !pick) return null;

  const odds       = pick.odds || 2.0;
  const confidence = pick.confidence || 0;  // 1-10
  const confPct    = confidence ? Math.round(confidence * 10) : null;
  const potReturn  = Math.round(betAmount * odds);
  const profit     = potReturn - betAmount;
  const sport      = pick.sport || "sports";
  const sportEmoji = { baseball: "⚾", basketball: "🏀", football: "⚽", nfl: "🏈", tennis: "🎾" }[sport] || "🏆";

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center"
      style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(6px)",
        animation: closing ? "pvf-bg-out 0.28s ease forwards" : "pvf-bg-in 0.3s ease" }}
      onClick={dismiss}
      data-testid="pick-vip-featured-modal"
    >
      {/* ── Card ── */}
      <div
        className="relative w-full sm:max-w-[420px] overflow-hidden"
        style={{
          background: "linear-gradient(175deg, #1C1509 0%, #0D0B08 55%, #1C1509 100%)",
          borderTop: "2px solid #D4AF37",
          borderLeft: "1.5px solid rgba(212,175,55,0.35)",
          borderRight: "1.5px solid rgba(212,175,55,0.35)",
          borderBottom: "1.5px solid rgba(212,175,55,0.25)",
          borderRadius: "28px 28px 28px 28px",
          boxShadow: "0 -4px 80px rgba(212,175,55,0.22), 0 30px 60px rgba(0,0,0,0.7)",
          animation: closing
            ? "pvf-card-out 0.26s ease forwards"
            : "pvf-card-in 0.45s cubic-bezier(0.22,1,0.36,1)",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* shimmer top line */}
        <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden">
          <div style={{ width: "100%", height: "100%",
            background: "linear-gradient(90deg, transparent 0%, #D4AF37 40%, #FFF3A3 50%, #D4AF37 60%, transparent 100%)",
            animation: "pvf-shimmer 2.8s ease-in-out infinite" }} />
        </div>

        {/* Close button */}
        <button onClick={dismiss}
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center z-10 transition-all"
          style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}
          aria-label="Cerrar"
          data-testid="pick-vip-modal-close">
          <X weight="bold" className="w-4 h-4 text-zinc-400" />
        </button>

        <div className="px-6 pt-7 pb-7 space-y-5">

          {/* ── HEADER: "Ronald publicó" ── */}
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-black"
                style={{ background: "linear-gradient(135deg,#D4AF37,#8B6914)", boxShadow: "0 4px 24px rgba(212,175,55,0.4)" }}>
                R
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 border-2 border-[#0D0B08] flex items-center justify-center">
                <Lightning weight="fill" className="w-2.5 h-2.5 text-white" />
              </div>
            </div>
            {/* Text */}
            <div className="flex-1 min-w-0 pt-0.5">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-[10px] font-black tracking-[0.3em] uppercase"
                  style={{ color: "#D4AF37" }}>Pick VIP</span>
                <span className="text-[10px] bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-full px-2 py-0.5 font-bold">
                  NUEVO
                </span>
              </div>
              <p className="text-base font-black text-white leading-tight">
                Ronald acaba de publicar una jugada exclusiva
              </p>
              <p className="text-xs text-zinc-500 mt-0.5">Solo disponible para quienes la desbloqueen</p>
            </div>
          </div>

          {/* ── MATCH INFO ── */}
          <div className="rounded-2xl overflow-hidden"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(212,175,55,0.15)" }}>
            <div className="px-4 pt-3 pb-2">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-base">{sportEmoji}</span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  {pick.league || "Partido"}
                </span>
                {pick.match_date && (
                  <span className="ml-auto text-[10px] text-zinc-600">
                    {new Date(pick.match_date).toLocaleDateString("es-ES",
                      { weekday: "short", day: "numeric", month: "short" })}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-white truncate flex-1">{pick.away_team}</span>
                <span className="text-xs text-zinc-600 font-mono px-2">VS</span>
                <span className="text-sm font-bold text-white truncate flex-1 text-right">{pick.home_team}</span>
              </div>
            </div>

            {/* Locked preview — blurred pick content */}
            <div className="relative mx-3 mb-3 mt-1 rounded-xl overflow-hidden"
              style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(212,175,55,0.12)", minHeight: 52 }}>
              <div className="px-4 py-3 select-none" style={{ filter: "blur(7px)", userSelect: "none" }}>
                <div className="text-xs font-bold text-amber-400 mb-0.5">Selección:</div>
                <div className="text-base font-black text-white">
                  {pick.pick_selection || pick.market || "Apuesta especial premium"}
                </div>
                <div className="text-xs text-zinc-400 mt-0.5">{pick.market || "Mercado"} · Cuota {odds}</div>
              </div>
              {/* Lock overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1"
                style={{ backdropFilter: "blur(2px)" }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.4)" }}>
                  <Lock weight="fill" className="w-3.5 h-3.5" style={{ color: "#D4AF37" }} />
                </div>
                <span className="text-[10px] font-bold tracking-widest text-zinc-500 uppercase">Bloqueado</span>
              </div>
            </div>
          </div>

          {/* ── STATS PILLS ── */}
          <div className="flex gap-2 flex-wrap">
            {winRate !== null && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)" }}>
                <Star weight="fill" className="w-3 h-3 text-emerald-400" />
                <span className="text-xs font-bold text-emerald-400">{winRate}% efectividad</span>
              </div>
            )}
            {confPct && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.25)" }}>
                <Lightning weight="fill" className="w-3 h-3" style={{ color: "#D4AF37" }} />
                <span className="text-xs font-bold" style={{ color: "#D4AF37" }}>{confPct}% confianza</span>
              </div>
            )}
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
              style={{ background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.25)" }}>
              <ChartLineUp weight="bold" className="w-3 h-3 text-blue-400" />
              <span className="text-xs font-bold text-blue-400">Cuota {odds}</span>
            </div>
          </div>

          {/* ── POTENTIAL RETURN CALCULATOR ── */}
          <div className="rounded-2xl p-4"
            style={{ background: "rgba(16,185,129,0.05)", border: "1px solid rgba(16,185,129,0.15)" }}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3 font-bold">
              Calculadora de retorno
            </div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs text-zinc-500">Si juegas</span>
              <div className="flex items-center gap-1 rounded-xl px-2 py-1 flex-1"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}>
                <span className="text-xs text-zinc-400">$</span>
                <input
                  type="number"
                  value={betAmount}
                  onChange={e => setBetAmount(Math.max(1, parseInt(e.target.value) || 1))}
                  min={1} max={9999}
                  className="bg-transparent text-white text-sm font-bold w-full outline-none"
                  onClick={e => e.stopPropagation()}
                  style={{ WebkitAppearance: "none", MozAppearance: "textfield" }}
                />
              </div>
              <span className="text-xs text-zinc-500">ganas</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-black text-emerald-400">${potReturn}</span>
              <div className="text-xs text-emerald-500 font-semibold">
                +${profit} de ganancia
              </div>
            </div>
          </div>

          {/* ── PRICE + CTA ── */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Precio único</div>
                <div className="text-4xl font-black tabular-nums" style={{ color: "#D4AF37", lineHeight: 1.1 }}>
                  ${(pick.pick_price || 0).toFixed(0)}
                  <span className="text-base font-normal text-zinc-600 ml-1">USD</span>
                </div>
              </div>
              <div className="text-right text-xs text-zinc-600 max-w-[120px]">
                Acceso completo hasta el final del partido
              </div>
            </div>

            <Link
              to={`/picks/${pick.id}`}
              onClick={dismiss}
              className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl font-black text-sm tracking-widest uppercase transition-all active:scale-95"
              style={{
                background: "linear-gradient(135deg, #D4AF37 0%, #B8941F 50%, #D4AF37 100%)",
                backgroundSize: "200% 100%",
                color: "#0A0A0F",
                boxShadow: "0 8px 32px -6px rgba(212,175,55,0.55)",
                animation: "pvf-gradient-move 3s ease-in-out infinite",
              }}
              data-testid="pick-vip-modal-cta"
            >
              <Lock weight="fill" className="w-4 h-4" />
              Desbloquear — ${(pick.pick_price || 0).toFixed(0)}
            </Link>

            <button
              onClick={dismiss}
              className="w-full text-xs text-zinc-600 hover:text-zinc-400 py-1 transition-colors"
              data-testid="pick-vip-modal-later"
            >
              No me interesa ahora
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pvf-bg-in       { from { opacity: 0 } to { opacity: 1 } }
        @keyframes pvf-bg-out      { from { opacity: 1 } to { opacity: 0 } }
        @keyframes pvf-card-in     { from { opacity: 0; transform: translateY(60px) scale(0.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes pvf-card-out    { from { opacity: 1; transform: translateY(0) scale(1); } to { opacity: 0; transform: translateY(40px) scale(0.97); } }
        @keyframes pvf-shimmer     { 0%,100% { transform: translateX(-150%); } 50% { transform: translateX(150%); } }
        @keyframes pvf-gradient-move { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
      `}</style>
    </div>
  );
}
